// src/components/achievements/AchievementFilter.tsx
import React, { useState } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

// Filtertypen definieren
export type FilterType = 'all' | 'completed' | 'incomplete';

interface AchievementFilterProps {
  currentFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  // Statistiken für die Anzeige der Anzahl der Erfolge für jeden Filter
  totalCount: number;
  completedCount: number;
}

const AchievementFilter: React.FC<AchievementFilterProps> = ({
  currentFilter,
  onFilterChange,
  totalCount,
  completedCount
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  
  // Filter-Label bestimmen
  const getFilterLabel = () => {
    switch(currentFilter) {
      case 'completed': return t('achievements.completed');
      case 'incomplete': return t('achievements.incomplete');
      default: return t('achievements.all');
    }
  };
  
  // Filter anwenden und Dropdown schließen
  const applyFilter = (filter: FilterType) => {
    onFilterChange(filter);
    setIsOpen(false);
  };
  
  // Anzahl der unvollständigen Erfolge berechnen
  const incompleteCount = totalCount - completedCount;

  return (
    <div className="relative">
      {/* Filter-Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center px-4 py-2 rounded-md text-sm
          ${currentFilter !== 'all' 
            ? 'bg-destiny-blue text-white' 
            : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
          } transition-colors duration-200`}
        title={t('achievements.filterAchievements')}
      >
        <FunnelIcon className="h-4 w-4 mr-2" />
        <span>{getFilterLabel()}</span>
        {currentFilter !== 'all' && (
          <span className="ml-2 px-2 py-0.5 bg-gray-800 bg-opacity-40 rounded-full text-xs">
            {currentFilter === 'completed' ? completedCount : incompleteCount}
          </span>
        )}
      </button>
      
      {/* Filter-Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 z-50 w-56 rounded-md shadow-lg bg-gray-800 ring-1 ring-gray-700 ring-opacity-50">
          <div className="py-1" role="menu">
            <button
              onClick={() => applyFilter('all')}
              className={`block w-full text-left px-4 py-2 text-sm ${
                currentFilter === 'all' 
                  ? 'bg-gray-700 text-destiny-gold' 
                  : 'text-white hover:bg-gray-700'
              }`}
            >
              {t('achievements.showAll')}
              <span className="ml-1 text-gray-400">({totalCount})</span>
            </button>
            
            <button
              onClick={() => applyFilter('completed')}
              className={`block w-full text-left px-4 py-2 text-sm ${
                currentFilter === 'completed' 
                  ? 'bg-gray-700 text-destiny-gold' 
                  : 'text-white hover:bg-gray-700'
              }`}
            >
              {t('achievements.showCompletedOnly')}
              <span className="ml-1 text-gray-400">({completedCount})</span>
            </button>
            
            <button
              onClick={() => applyFilter('incomplete')}
              className={`block w-full text-left px-4 py-2 text-sm ${
                currentFilter === 'incomplete' 
                  ? 'bg-gray-700 text-destiny-gold' 
                  : 'text-white hover:bg-gray-700'
              }`}
            >
              {t('achievements.showIncompleteOnly')}
              <span className="ml-1 text-gray-400">({incompleteCount})</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementFilter;